import React, { ReactNode } from "react";
import i18n from "../i18n";
import CountryService, { ICountry, DEFAULT_COUNTRIES } from "../services/CountryService";

export const COUNTRY_STORAGE_KEY = "muradi_country";
export const LANGUAGE_STORAGE_KEY = "muradi_language";

export interface ILanguage {
  code: string;
  name: string;
  nativeName: string;
}

export const LANGUAGES: ILanguage[] = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "ur", name: "Urdu", nativeName: "اردو" },
  { code: "bn", name: "Bangla", nativeName: "বাংলা" },
  { code: "sd", name: "Sindhi", nativeName: "سنڌي" },
  { code: "es", name: "Spanish", nativeName: "Español" },
];

const DEFAULT_COUNTRY_CODE = "PK";
const DEFAULT_LANGUAGE_CODE = "en";

type ContextObj = {
  country: string;
  language: string;
  countries: ICountry[];
  languages: ILanguage[];
  setCountry: (code: string) => void;
  setLanguage: (code: string) => void;
};

const LocaleContext = React.createContext<ContextObj>({
  country: DEFAULT_COUNTRY_CODE,
  language: DEFAULT_LANGUAGE_CODE,
  countries: DEFAULT_COUNTRIES,
  languages: LANGUAGES,
  setCountry: () => {},
  setLanguage: () => {},
});

interface LocaleProviderProps {
  children: ReactNode;
}

export const LocaleContextProvider: React.FC<LocaleProviderProps> = ({ children }) => {
  const [country, setCountryState] = React.useState<string>(
    () => localStorage.getItem(COUNTRY_STORAGE_KEY) || DEFAULT_COUNTRY_CODE
  );
  const [language, setLanguageState] = React.useState<string>(
    () => localStorage.getItem(LANGUAGE_STORAGE_KEY) || DEFAULT_LANGUAGE_CODE
  );
  const [countries, setCountries] = React.useState<ICountry[]>(DEFAULT_COUNTRIES);

  React.useEffect(() => {
    if (!localStorage.getItem(COUNTRY_STORAGE_KEY)) {
      localStorage.setItem(COUNTRY_STORAGE_KEY, DEFAULT_COUNTRY_CODE);
    }
    if (!localStorage.getItem(LANGUAGE_STORAGE_KEY)) {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, DEFAULT_LANGUAGE_CODE);
    }
  }, []);

  React.useEffect(() => {
    CountryService.getCountries()
      .then((list) => setCountries(list.length ? list : DEFAULT_COUNTRIES))
      .catch(() => setCountries(DEFAULT_COUNTRIES));
  }, []);

  const setCountry = React.useCallback((code: string) => {
    const normalized = (code || DEFAULT_COUNTRY_CODE).toUpperCase();
    localStorage.setItem(COUNTRY_STORAGE_KEY, normalized);
    setCountryState(normalized);
  }, []);

  const setLanguage = React.useCallback((code: string) => {
    const normalized = (code || DEFAULT_LANGUAGE_CODE).toLowerCase();
    localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
    setLanguageState(normalized);

    i18n.changeLanguage(normalized);
  }, []);

  const contextValue: ContextObj = {
    country,
    language,
    countries,
    languages: LANGUAGES,
    setCountry,
    setLanguage,
  };

  return <LocaleContext.Provider value={contextValue}>{children}</LocaleContext.Provider>;
};

export default LocaleContext;
